import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllStockDumpReportData,
  getStockDumpReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { stockMovementTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "productName",
  "qty",
  "averageDailySales",
  "grossMargin",
  "movementType",
  "averagePurchasePrice",
  "averageSellingPrice",
  "unitsSold",
  "unitsPurchased",
  "grossMarginPercentage",
  "tradeName",
  "category",
  "subCategory",
  "hsnCode",
  "barcodeNo",
  "brand",
  "reorderLevel",
];
const initSearch = {
  productName: "",
  movementType: [],
  category: [],
  subcategory: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Qty",
  "Avg. Daily Sales",
  "Gross Margin",
  "Avg. Purchase Price",
  "Avg. Selling Price",
  "Units Sold",
  "Units Purchased",
  "Gross Margin Percentage",
  "Trade Name",
  "HSN Code",
  "Barcode Number",
  "Brand",
  "Reorder Level",
];
const splitRows = [
  "Qty",
  "Avg. Daily Sales",
  "Gross Margin",
  "Avg. Purchase Price",
  "Avg. Selling Price",
  "Units Sold",
  "Units Purchased",
  "Gross Margin Percentage",
];

const StockDumpReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { stockDump, stockDumpAll } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllStockDumpReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.productName,
    searchValues?.movementType,
    searchValues?.category,
    searchValues?.subcategory,
  ]);

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "dump",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (searchValues?.productName?.length > 0) {
      reqObj.productName = [searchValues?.productName];
    }
    if (searchValues?.movementType?.length > 0) {
      reqObj.movementType = searchValues?.movementType?.map((li) => li?.value);
    }
    if (searchValues?.category?.length > 0) {
      reqObj.category = searchValues?.category?.map((li) => li?.value);
    }
    if (searchValues?.subcategory?.length > 0) {
      reqObj.subcategory = searchValues?.subcategory?.map((li) => li?.value);
    }

    if (filterValues?.selectedList?.includes("Trade Name")) {
      reqObj.tradeName = [filterValues?.values?.["Trade Name"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("HSN Code")) {
      reqObj.hsnSacCode = [filterValues?.values?.["HSN Code"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Barcode Number")) {
      reqObj.barcodeNo = [filterValues?.values?.["Barcode Number"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Brand")) {
      reqObj.brand = [filterValues?.values?.["Brand"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Reorder Level")) {
      reqObj.reorderLevel = [filterValues?.values?.["Reorder Level"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Qty")) {
      reqObj.minQty = Number(filterValues?.values?.["Min Qty"] ?? 0);
      reqObj.maxQty = Number(filterValues?.values?.["Max Qty"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Avg. Daily Sales")) {
      reqObj.minDailySales = Number(
        filterValues?.values?.["Min Daily Sales"] ?? 0,
      );
      reqObj.maxDailySales = Number(
        filterValues?.values?.["Max Daily Sales"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Gross Margin")) {
      reqObj.minGrossMargin = Number(
        filterValues?.values?.["Min Gross Margin"] ?? 0,
      );
      reqObj.maxGrossMargin = Number(
        filterValues?.values?.["Max Gross Margin"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Avg. Purchase Price")) {
      reqObj.minPurchasePrice = Number(
        filterValues?.values?.["Min Purchase Price"] ?? 0,
      );
      reqObj.maxPurchasePrice = Number(
        filterValues?.values?.["Max Purchase Price"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Avg. Selling Price")) {
      reqObj.minSellingPrice = Number(
        filterValues?.values?.["Min Selling Price"] ?? 0,
      );
      reqObj.maxSellingPrice = Number(
        filterValues?.values?.["Max Selling Price"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Units Sold")) {
      reqObj.minUnitsSold = Number(
        filterValues?.values?.["Min Units Sold"] ?? 0,
      );
      reqObj.maxUnitsSold = Number(
        filterValues?.values?.["Max Units Sold"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Units Purchased")) {
      reqObj.minUnitsPurchased = Number(
        filterValues?.values?.["Min Units Purchased"] ?? 0,
      );
      reqObj.maxUnitsPurchased = Number(
        filterValues?.values?.["Max Units Purchased"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Gross Margin Percentage")) {
      reqObj.minGrossMarginPercentage = Number(
        filterValues?.values?.["Min Gross Margin %"] ?? 0,
      );
      reqObj.maxGrossMarginPercentage = Number(
        filterValues?.values?.["Max Gross Margin %"] ?? 0,
      );
    }

    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getStockDumpReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((stockDump?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return (stockDump?.data ?? [])?.map((li) => ({
      ...li,
      averagePurchasePrice: Number(li?.averagePurchasePrice ?? 0)?.toFixed(2),
      averageSellingPrice: Number(li?.averageSellingPrice ?? 0)?.toFixed(2),
      grossMargin: Number(li?.grossMargin ?? 0)?.toFixed(2),
      grossMarginAmount: Number(li?.grossMarginAmount ?? 0)?.toFixed(2),
      averageDailySales: Number(li?.averageDailySales ?? 0)?.toFixed(2),
      hsnCode: li?.hsnSacCode,
      qty: li?.quantityAsOnToday,
      movementType: (
        <div
          className={`con-fit fs12 white-color ph10 pv2 br15 ${li?.movementType?.toLowerCase() === "normal" ? "card-con-green-back" : li?.movementType?.toLowerCase() === "fast-moving" ? "card-top-yellow-color" : "card-top-orange-color"}`}
        >
          {li?.movementType}
        </div>
      ),
    }));
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const getProductNames = () => {
    const reqList = (stockDumpAll?.data ?? [])
      ?.map((li) => li?.productName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getCategoryList = () => {
    const reqList = (stockDumpAll?.data ?? [])
      ?.map((li) => li?.category)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getSubCategoryList = () => {
    const reqList = (stockDumpAll?.data ?? [])
      ?.map((li) => li?.subCategory)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const getFieldName = (name, type) => {
    if (name === "Qty") {
      if (type === "start") return "Min Qty";
      return "Max Qty";
    }
    if (name === "Avg. Daily Sales") {
      if (type === "start") return "Min Daily Sales";
      return "Max Daily Sales";
    }
    if (name === "Gross Margin") {
      if (type === "start") return "Min Gross Margin";
      return "Max Gross Margin";
    }
    if (name === "Avg. Purchase Price") {
      if (type === "start") return "Min Purchase Price";
      return "Max Purchase Price";
    }
    if (name === "Avg. Selling Price") {
      if (type === "start") return "Min Selling Price";
      return "Max Selling Price";
    }
    if (name === "Units Sold") {
      if (type === "start") return "Min Units Sold";
      return "Max Units Sold";
    }
    if (name === "Units Purchased") {
      if (type === "start") return "Min Units Purchased";
      return "Max Units Purchased";
    }
    if (name === "Gross Margin Percentage") {
      if (type === "start") return "Min Gross Margin %";
      return "Max Gross Margin %";
    }
  };

  const getSuggestionList = (typ) => {
    const reqType =
      typ === "Trade Name"
        ? "tradeName"
        : typ === "HSN Code"
          ? "hsnSacCode"
          : typ === "Barcode Number"
            ? "barcodeNo"
            : typ === "Brand"
              ? "brand"
              : typ === "Reorder Level"
                ? "reorderLevel"
                : "";
    const reqList = (stockDumpAll?.data ?? [])
      ?.map((li) => (li?.[reqType] ?? "")?.toString())
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  return (
    <>
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getProductNames()}
              value={searchValues?.productName}
              name="productName"
              handleChange={onSearchChange}
              label="Product Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={stockMovementTypes}
              value={searchValues?.movementType ?? []}
              name="movementType"
              handleChange={onSearchChange}
              label="Movement Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getCategoryList()}
              value={searchValues?.category ?? []}
              name="category"
              handleChange={onSearchChange}
              label="Category"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getSubCategoryList()}
              value={searchValues?.subcategory ?? []}
              name="subcategory"
              handleChange={onSearchChange}
              label="Subcategory"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {stockDump?.totalRecords ?? 0}
      </Typography>
      <div className="mtMin10">
        <ReportTable datas={getTableData()} columns={crntTblHeders} />
        <div className="flex1-end mtMin30">
          {Math.ceil((stockDump?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => {
                      return (
                        <div className="mt20">
                          {splitRows?.includes(li) ? (
                            <div className="flex-row-between-align-center">
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "start")}
                                  fullWidth
                                  name={getFieldName(li, "start")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "start")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "start")}
                                  mobileDate={li === "Appt Date"}
                                  type="number"
                                />
                              </div>
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "end")}
                                  fullWidth
                                  name={getFieldName(li, "end")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "end")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "end")}
                                  mobileDate={li === "Appt Date"}
                                  type="number"
                                />
                              </div>
                            </div>
                          ) : (
                            <CustomTextField
                              placeholder={li}
                              fullWidth
                              name={li}
                              handleChange={onFilterValuesChange}
                              value={filterValues?.values?.[li] ?? ""}
                              label={li}
                              suggetions
                              options={getSuggestionList(li)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default StockDumpReport;
